import { usePageModels } from '@/context/PageModels';
import Button from '@/features/shared/Button';
import ContentCard from '@/features/shared/ContentCard';
import { ChipFilters, SortBy } from '@/features/shared/ElasticComponent';
import ElasticComponentContext from '@/features/shared/ElasticComponent/ElasticComponentContext';
import { useDataLayerSearchEvents } from '@/features/shared/Hooks/data-layer';
import { trackSearchClick } from '@/features/shared/Hooks/useElasticSearch';
import {
  MuiBox,
  MuiContainer,
  MuiGrid,
  MuiPagination,
  MuiPaginationItem,
  MuiTypography,
} from '@/features/shared/material-ui';
import { SearchMeta } from '@/features/shared/SearchMeta';
import { IS_PREVIEW } from '@/utils/constants';
import {
  getPageUrl,
  getResultLanguageURI,
  mapColorNameToContrastColorName,
  setPageLink,
} from '@/utils/helpers';
import { parseStringBool } from '@/utils/parse';
import { resolveDate, resolveImageUrl } from '@/utils/resolvers';
import { BrPageContext } from '@bloomreach/react-sdk';
import clsx from 'clsx';
import { cond, get, pipe, reduce, stubTrue } from 'lodash/fp';
import React, { ChangeEvent, memo, useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { CardResult, CardSkeleton } from '../Card';
import { ResultsPlaceholder } from './Results.placeholder';
import useStyles from './Results.styles';
import {
  evaluateFallbackImageTo,
  getPartnerResourceAssetName,
  isDownloadableAsset,
  PARTNER_RESOURCES_ASSETS_TYPES,
  resolveCategories,
  resolveESArray,
} from './Results.utils';

const mapResultsToCardResults = ({
  results = [],
  page,
  assetImageUrlMap,
  contentCategoryCardFieldMapper,
  contentCategoryKey = '',
  language,
  contentCategoryCardDateFieldMapper,
}: {
  results: any[];
  page?: Br.Page;
  assetImageUrlMap: any;
  contentCategoryCardFieldMapper?: Record<string, string>;
  contentCategoryKey?: string;
  language?: string;
  contentCategoryCardDateFieldMapper?: Record<string, string>;
}): CardType[] => {
  return results.map((itemResult) => {
    const cardCategoryField = contentCategoryCardFieldMapper?.[contentCategoryKey || ''] || '';
    const cardCategory = resolveCategories({ raw: itemResult, categoryType: cardCategoryField });
    const cardContentCategory = resolveESArray(itemResult?.tax_content_category?.raw);
    const dateValueField =
      contentCategoryCardDateFieldMapper?.[contentCategoryKey] || 'publication_date';
    const cardImageVariant = {
      url: cond([
        [isDownloadableAsset, evaluateFallbackImageTo(cardCategory, assetImageUrlMap, page)], // add a fallback image
        [stubTrue, get(['image', 'raw'])], // return the original image value
      ])(itemResult),
    };

    return {
      cardDescription: itemResult.description?.raw,
      cardHeading: itemResult.title?.raw,
      cardImage: {
        original: cardImageVariant,
        featuredSmall: cardImageVariant,
        featuredMedium: cardImageVariant,
        featuredLarge: cardImageVariant,
      } as ImageType,
      cardCategory,
      cardContentCategory: cardContentCategory[0] || '',
      cardPublicationDate: resolveDate({ rawVal: itemResult?.[dateValueField]?.raw }),
      cardReadTime: itemResult.read_time?.raw,
      url: parseStringBool(itemResult?.download?.raw)
        ? itemResult?.url?.raw
        : page && getPageUrl({ url: getResultLanguageURI(itemResult, language), page }),
      cardCategoryType: itemResult?.[cardCategoryField] ? cardCategoryField : '',
      resultID: itemResult?.id?.raw,
      isPartnerOnly: parseStringBool(itemResult?.is_partner_only?.raw),
      download: parseStringBool(itemResult?.download?.raw),
    };
  });
};

const ResultsGrid = ({
  presentation,
  theme,
  labels,
  categoryUrl,
  isCached,
  contentCategoryCardFieldMapper,
  contentCategoryKey,
}: any) => {
  const page = useContext(BrPageContext);
  const { config, language, contentCategoryCardDateFieldMapper } = usePageModels();
  const { cardStyle, includeCardImages: showImages, containerBackgroundColor } = presentation;
  const isGrid = useMemo(() => /grid/.test(cardStyle), [cardStyle]);
  const classes = useStyles({});
  const {
    results = [],
    pagination,
    resetParams = () => null,
    forceUpdate = () => null,
    updateQueryParams,
    status,
    queryParams,
    engine,
    requestID,
    searchKey = '',
  } = useContext(ElasticComponentContext);
  const isLoading = useMemo(() => /loading/i.test(status) && !isCached, [status, isCached]);
  const topDivRef = React.useRef(document.createElement('div'));

  const assetImageUrlMap = useMemo(
    () =>
      reduce(
        (prev: any, assetType: string) => ({
          ...prev,
          [assetType]: pipe<any, any, any>(
            () => get(getPartnerResourceAssetName(assetType), config?.elasticsearch),
            (imageUrl) => resolveImageUrl({ raw: imageUrl }),
          )(assetType),
        }),
        {},
      )(PARTNER_RESOURCES_ASSETS_TYPES),
    [config?.elasticsearch],
  );

  const cardResults = useMemo(
    () =>
      mapResultsToCardResults({
        results,
        page,
        assetImageUrlMap,
        contentCategoryCardFieldMapper,
        contentCategoryKey,
        language,
        contentCategoryCardDateFieldMapper,
      }),
    [
      results,
      page,
      assetImageUrlMap,
      contentCategoryCardFieldMapper,
      contentCategoryKey,
      contentCategoryCardDateFieldMapper,
      language,
    ],
  );
  const cardColor = useMemo(
    () => mapColorNameToContrastColorName({ colorName: containerBackgroundColor }),
    [containerBackgroundColor],
  );
  const chipColor = useMemo(
    () => mapColorNameToContrastColorName({ colorName: containerBackgroundColor }),
    [containerBackgroundColor],
  );

  const resetFilters = () => resetParams();

  const { triggerSearchEventDataLayer } = useDataLayerSearchEvents('page_nav_click');

  const handlePagination = (event: ChangeEvent<unknown>, value: number) => {
    event.preventDefault();

    updateQueryParams('page', String(value));
    topDivRef?.current.scrollIntoView();
    triggerSearchEventDataLayer();
  };

  const handleResultClicks = (evt: React.MouseEvent) => {
    const target = evt.target as HTMLElement;
    const a = target?.closest('a');

    if (a?.dataset?.resultid) {
      const { resultid } = a.dataset;
      trackSearchClick(searchKey, queryParams.q, engine, resultid, requestID);
    }
  };

  const resultsWithImage = useMemo(
    () =>
      cardResults.map((result): CardType => {
        return {
          ...result,
          cardImage: showImages ? result.cardImage : undefined,
        };
      }),
    [cardResults, showImages],
  );

  const rootClassName = clsx(classes.root, `${theme}-theme`, isGrid && 'is-grid');
  const isCMSPreview = IS_PREVIEW && resultsWithImage.length < 1;

  return (
    <div className={rootClassName} ref={topDivRef}>
      {isCMSPreview && (
        <MuiContainer className={classes.resultsContainer} onClick={handleResultClicks}>
          <ResultsPlaceholder theme={theme} cardColor={cardColor} itemCount={6} row={!isGrid} />
        </MuiContainer>
      )}

      {isLoading || resultsWithImage.length > 0 ? (
        <>
          <MuiContainer>
            <div className={classes.topResults}>
              <ChipFilters
                chipColor={chipColor}
                onDelete={() => {
                  forceUpdate();
                }}
                hoverable={false}
                theme={theme}
              />
              <div className={classes.spacer} />
              {presentation?.isSortShown && <SortBy theme={theme} className={classes.sortBy} />}
            </div>
          </MuiContainer>
          {/* This <div> is handling bubbled clicks */}
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
          <MuiContainer className={classes.resultsContainer} onClick={handleResultClicks}>
            <MuiGrid container spacing={3} className={classes.grid}>
              {(isLoading ? new Array(isGrid ? 3 : 1).fill(null) : resultsWithImage).map(
                (result, idx: number) => (
                  <MuiGrid
                    // eslint-disable-next-line react/no-array-index-key
                    key={result?.data?.cardHeading || `card_${idx}`}
                    item
                    xs={12}
                    sm={isGrid ? 6 : 12}
                    md={isGrid ? 6 : 12}
                    lg={isGrid ? 4 : 12}
                    className={classes.block}
                  >
                    {isGrid ? (
                      <>
                        {isLoading ? (
                          <CardSkeleton image={showImages} theme={theme} cardColor={cardColor} />
                        ) : (
                          <ContentCard
                            data={{
                              ...result,
                              variant: showImages ? 'image' : '',
                              isDownloadable: result.download,
                            }}
                            cardColor={cardColor}
                            theme={theme}
                            categoryUrl={categoryUrl}
                            labels={labels}
                          />
                        )}
                      </>
                    ) : (
                      <>
                        {isCMSPreview && (
                          <CardSkeleton
                            image={showImages}
                            theme={theme}
                            row
                            cardColor={cardColor}
                            animated={false}
                          />
                        )}
                        {isLoading ? (
                          <CardSkeleton
                            image={showImages}
                            row
                            theme={theme}
                            cardColor={cardColor}
                          />
                        ) : (
                          <CardResult
                            title={result.cardHeading}
                            image={result?.cardImage?.original?.url}
                            description={result.cardDescription}
                            url={result.url}
                            // tags={result.keywords.raw}
                            date={result.cardPublicationDate}
                            theme={theme}
                            cardColor={cardColor}
                            category={result.cardCategory}
                            categoryType={result.cardCategoryType}
                            categoryUrl={categoryUrl}
                            contentCategory={result.cardContentCategory}
                            labels={labels}
                            isPartnerOnly={result.isPartnerOnly}
                            isDownloadable={result.download}
                          />
                        )}
                      </>
                    )}
                  </MuiGrid>
                ),
              )}
            </MuiGrid>
            {!isLoading && (
              <>
                <MuiPagination
                  className={clsx(classes.pagination, 'gtm-pagination-container')}
                  count={pagination.totalPages}
                  page={pagination.currentPage}
                  onChange={handlePagination}
                  renderItem={(item: any) => (
                    <MuiPaginationItem component={Link} to={setPageLink(item.page)} {...item} />
                  )}
                />
                <SearchMeta paging={pagination} query={queryParams} />
              </>
            )}
          </MuiContainer>
        </>
      ) : (
        <div className={classes.noResults}>
          <MuiBox marginBottom={2}>
            <MuiTypography variant="h5" className={classes.noResultsMessage}>
              {labels?.sorry_no_results || 'Sorry, we couldn’t find any results'}
            </MuiTypography>
          </MuiBox>
          <Button outlined onClick={resetFilters} className={classes.noResultsCta} theme={theme}>
            {labels?.reset_filters || 'Reset all filters'}
          </Button>
          <SearchMeta paging={pagination} query={queryParams} />
        </div>
      )}
    </div>
  );
};

export default memo(ResultsGrid);
